<template>
  <div class="guide-article-page">
    <HeadNav selectedLanguage="en" page="guides" />

    <main class="container">
      <nav class="breadcrumb" aria-label="Breadcrumb">
        <router-link to="/">Home</router-link>
        <span class="sep" aria-hidden="true">/</span>
        <router-link to="/guides/">Guides</router-link>
        <span class="sep" aria-hidden="true">/</span>
        <span class="current">{{ guide.h1 }}</span>
      </nav>

      <article class="article">
        <header class="article-header">
          <h1>{{ guide.h1 }}</h1>
          <div class="article-meta">
            <time :datetime="guide.date">{{ formatDate(guide.date) }}</time>
            <span class="meta-dot" aria-hidden="true" />
            <span>MyVPNBook</span>
          </div>
        </header>

        <figure class="cover">
          <img :src="guide.poster" :alt="guide.h1" loading="eager">
        </figure>

        <p v-if="guide.lead" class="lead">
          <strong>{{ guide.lead.strong }}</strong>
          {{ guide.lead.text }}
        </p>

        <template v-if="guide.blocks && guide.blocks.length">
          <template v-for="(block, idx) in guide.blocks">
            <h2 v-if="block.type === 'h2'" :key="`h2-${idx}`">{{ block.text }}</h2>
            <h3 v-else-if="block.type === 'h3'" :key="`h3-${idx}`">{{ block.text }}</h3>
            <p
              v-else-if="block.type === 'p' && block.html"
              :key="`phtml-${idx}`"
              v-html="block.html"
            />
            <p v-else-if="block.type === 'p'" :key="`p-${idx}`">{{ block.text }}</p>
            <ul v-else-if="block.type === 'ul'" :key="`ul-${idx}`" class="check-list">
              <li v-for="(item, iIdx) in block.items" :key="`ul-${idx}-${iIdx}`" v-html="item" />
            </ul>
            <ol
              v-else-if="block.type === 'ol'"
              :key="`ol-${idx}`"
              class="step-list"
              :start="block.start || 1"
            >
              <li v-for="(item, iIdx) in block.items" :key="`ol-${idx}-${iIdx}`" v-html="item" />
            </ol>
            <pre v-else-if="block.type === 'code'" :key="`code-${idx}`" class="code-block"><code>{{ block.text }}</code></pre>
            <figure
              v-else-if="block.type === 'img'"
              :key="`img-${idx}`"
              class="inline-figure"
              :class="{ 'is-compact': block.compact }"
            >
              <img :src="block.src" :alt="block.alt || guide.h1" loading="lazy">
            </figure>
            <div v-else-if="block.type === 'table'" :key="`table-${idx}`" class="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th v-for="header in block.headers" :key="header">{{ header }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rIdx) in block.rows" :key="rIdx">
                    <td v-for="(cell, cIdx) in row" :key="cIdx">{{ cell }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <article v-else-if="block.type === 'product'" :key="`product-${idx}`" class="product-card">
              <div class="product-title">
                <router-link v-if="block.link" :to="block.link">{{ block.title }}</router-link>
                <span v-else>{{ block.title }}</span>
              </div>
              <p v-if="block.bestFor" class="best-for"><strong>Best for:</strong> {{ block.bestFor }}</p>
              <div class="product-cols">
                <div class="col-card col-pros">
                  <h4>Pros</h4>
                  <ul>
                    <li v-for="item in block.pros" :key="item">{{ item }}</li>
                  </ul>
                </div>
                <div class="col-card col-cons">
                  <h4>Cons</h4>
                  <ul>
                    <li v-for="item in block.cons" :key="item">{{ item }}</li>
                  </ul>
                </div>
              </div>
              <p v-if="block.note">{{ block.note }}</p>
            </article>
            <div v-else-if="block.type === 'myth'" :key="`myth-${idx}`" class="myth-card">
              <h3>{{ block.title }}</h3>
              <p>{{ block.text }}</p>
            </div>
          </template>
        </template>

        <section
          v-for="(section, idx) in guide.sections || []"
          :key="`section-${idx}`"
          class="content-section"
        >
          <h2 v-if="section.heading">{{ section.heading }}</h2>
          <p v-for="(paragraph, pIdx) in section.paragraphs" :key="pIdx">{{ paragraph }}</p>
          <ul v-if="section.items" class="check-list">
            <li v-for="item in section.items" :key="item">{{ item }}</li>
          </ul>
          <p v-if="section.footer" class="section-footer">{{ section.footer }}</p>
        </section>

        <section v-if="guide.faq && guide.faq.length" class="faq-section">
          <div class="section-label">
            <h2>FAQ</h2>
            <p>Quick answers to common questions.</p>
          </div>
          <div class="faq-list">
            <div v-for="item in guide.faq" :key="item.question" class="faq-item">
              <h3>{{ item.question }}</h3>
              <p>{{ item.answer }}</p>
            </div>
          </div>
        </section>

        <section v-if="guide.conclusion" class="conclusion">
          <h2>{{ guide.conclusion.heading || 'Conclusion' }}</h2>
          <template v-if="guide.conclusion.paragraphs">
            <p v-for="(paragraph, idx) in guide.conclusion.paragraphs" :key="idx">{{ paragraph }}</p>
          </template>
          <p v-else>
            Secure VPN can be a safe option for everyday browsing
            <strong>{{ guide.conclusion.strong }}</strong>.
            For casual use such as securing public Wi-Fi or protecting your IP address, it provides useful encryption.
            If your priority is maximum privacy or handling highly sensitive information, review the provider's logging policy carefully or consider a well-established paid VPN service with independently audited no-logs claims.
          </p>
        </section>

        <section v-if="recommended.length" class="recommended">
          <h2>Recommended Reading</h2>
          <div class="recommended-list">
            <router-link
              v-for="item in recommended"
              :key="item.path"
              :to="item.path"
              class="recommended-card"
            >
              <img :src="item.poster" :alt="item.h1" loading="lazy">
              <div>
                <h3>{{ item.h1 }}</h3>
                <p>{{ item.description }}</p>
                <span>Read Guide →</span>
                <time :datetime="item.date">{{ formatDate(item.date) }}</time>
              </div>
            </router-link>
          </div>
        </section>

        <footer class="article-footer">
          <router-link class="back-link" to="/guides/">
            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M16 10H4M9 5l-5 5 5 5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Back to Guides
          </router-link>
        </footer>
      </article>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import '@/css/vpn-theme.scss';
import HeadNav from '@/components/HeadNav.vue';
import guides from '@/documents/myvpnbookGuides';

export default {
  name: 'GuideArticle',
  components: {
    HeadNav,
  },
  computed: {
    guide() {
      const slug = this.$route.meta.guideSlug;
      return guides.find((item) => item.slug === slug) || guides[0];
    },
    recommended() {
      return guides
        .filter((item) => item.slug !== this.guide.slug)
        .sort((a, b) => new Date(b.date) - new Date(a.date) || (a.order || 0) - (b.order || 0))
        .slice(0, 3);
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.recommended {
  margin-top: 36px;
}

.recommended-list {
  display: grid;
  gap: 16px;
}

.recommended-card {
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: 14px;
  padding: 12px;
  background: #fff;
  border: 1px solid var(--vpn-line, #e6e8ef);
  border-radius: 14px;
  text-decoration: none;
  color: inherit;
}

.recommended-card img {
  width: 120px;
  height: 80px;
  object-fit: cover;
  border-radius: 8px;
}

.recommended-card h3 {
  margin: 0 0 6px;
  font-size: 16px;
}

.recommended-card p,
.recommended-card time,
.recommended-card span {
  display: block;
  margin: 0 0 4px;
  color: var(--vpn-ink-muted, #5b6475);
  font-size: 13px;
}

.guide-article-page {
  min-height: 70vh;
  background:
    radial-gradient(ellipse 80% 45% at 8% -8%, rgba(91, 79, 232, 0.12), transparent 55%),
    radial-gradient(ellipse 50% 35% at 100% 0%, rgba(59, 105, 255, 0.08), transparent 50%),
    #f5f4fb;
  color: var(--vpn-ink);
  font-family: var(--vpn-font);
}

.container {
  width: var(--vpn-container);
  margin: 0 auto;
  padding: 28px 0 88px;
}

.breadcrumb {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 22px;
  font-size: 13px;
  font-weight: 600;
  color: var(--vpn-muted);
  animation: vpn-fade 0.5s ease both;
}

.breadcrumb a {
  color: var(--vpn-teal-deep);
  text-decoration: none;
}

.breadcrumb a:hover {
  color: var(--vpn-teal);
}

.sep {
  color: rgba(107, 127, 146, 0.55);
}

.current {
  color: var(--vpn-ink-soft);
}

.article {
  margin: 0 auto;
  padding: 40px 44px 36px;
  background: #fff;
  border: 1px solid var(--vpn-line);
  border-radius: 24px;
  box-shadow: var(--vpn-shadow-soft);
  animation: vpn-rise 0.65s ease both;
}

.article-header {
  margin-bottom: 28px;
  text-align: center;
}

h1 {
  margin: 0 0 16px;
  font-size: clamp(30px, 4.4vw, 42px);
  line-height: 1.15;
  letter-spacing: -0.03em;
  font-weight: 800;
}

.article-meta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 13px;
  font-weight: 700;
  color: var(--vpn-muted);
}

.meta-dot {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: rgba(107, 127, 146, 0.5);
}

.cover {
  margin: 0 0 28px;
  overflow: hidden;
  border-radius: 18px;
  background: linear-gradient(135deg, #12122e, #2a2a6a);
  box-shadow: 0 16px 36px rgba(18, 18, 46, 0.18);
}

.cover img {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
}

.lead {
  margin: 0 0 8px;
  padding: 18px 20px;
  border-left: 3px solid var(--vpn-teal);
  border-radius: 0 14px 14px 0;
  background: linear-gradient(90deg, rgba(91, 79, 232, 0.08), rgba(91, 79, 232, 0.02));
  font-size: 17px;
  line-height: 1.8;
  color: var(--vpn-ink-soft);
}

.content-section {
  padding-top: 8px;
}

.content-section + .content-section {
  margin-top: 8px;
  padding-top: 24px;
  border-top: 1px solid rgba(15, 48, 74, 0.06);
}

h2 {
  margin: 28px 0 14px;
  font-size: clamp(22px, 2.6vw, 26px);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: var(--vpn-ink);
}

h3 {
  margin: 22px 0 10px;
  font-size: 18px;
  font-weight: 800;
  color: var(--vpn-ink);
}

p,
li {
  font-size: 16px;
  line-height: 1.8;
  color: var(--vpn-ink-soft);
}

p {
  margin: 0 0 14px;
}

.article ::v-deep a {
  color: var(--vpn-teal-deep);
  font-weight: 700;
  text-decoration: none;
}

.article ::v-deep a:hover {
  color: var(--vpn-teal);
}

.section-footer {
  margin-top: 4px;
}

.check-list,
.step-list {
  margin: 0 0 18px;
  padding-left: 22px;
}

.check-list li,
.step-list li {
  margin: 0 0 6px;
  padding: 0;
  font-size: 16px;
  line-height: 1.7;
  color: var(--vpn-ink-soft);
}

.check-list li:last-child,
.step-list li:last-child {
  margin-bottom: 0;
}

.code-block {
  margin: 8px 0 18px;
  padding: 16px 18px;
  overflow-x: auto;
  border: 1px solid #1e293b;
  border-radius: 12px;
  background: #0f172a;
  color: #f8fafc;
  font-size: 15px;
  line-height: 1.7;
}

/* 覆盖全局 code 浅灰底样式，保证深色代码块文字清晰可读 */
.code-block code {
  display: block;
  margin: 0;
  padding: 0 !important;
  border-radius: 0;
  background: transparent !important;
  color: #f8fafc !important;
  font-family: Consolas, 'Courier New', monospace;
  font-size: inherit;
  font-weight: 500;
  letter-spacing: 0.02em;
  white-space: pre-wrap;
  word-break: break-word;
}

.inline-figure {
  margin: 8px auto 20px;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid var(--vpn-line);
  background: #f8fafc;
  max-width: 100%;
}

.inline-figure.is-compact {
  max-width: 420px;
}

.inline-figure img {
  display: block;
  width: 100%;
  height: auto;
}

.table-wrap {
  margin: 8px 0 20px;
  overflow-x: auto;
  border: 1px solid var(--vpn-line);
  border-radius: 16px;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 420px;
}

th,
td {
  padding: 14px 16px;
  text-align: left;
  border-bottom: 1px solid rgba(15, 48, 74, 0.06);
  font-size: 15px;
}

th {
  background: linear-gradient(135deg, rgba(91, 79, 232, 0.1), rgba(59, 105, 255, 0.06));
  color: var(--vpn-ink);
  font-weight: 800;
}

td {
  color: var(--vpn-ink-soft);
}

tr:last-child td {
  border-bottom: 0;
}

.product-card {
  margin: 10px 0 16px;
  padding: 16px 18px;
  border: 1px solid var(--vpn-line);
  border-radius: 14px;
  background: #fff;
}

.product-title {
  margin-bottom: 6px;
  font-size: 18px;
  font-weight: 800;
  color: var(--vpn-ink);
}

.product-title a {
  color: var(--vpn-teal-deep);
  text-decoration: none;
}

.product-title a:hover {
  color: var(--vpn-teal);
  text-decoration: underline;
}

.best-for {
  margin-bottom: 10px;
}

.product-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
}

.col-card {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid transparent;
}

.col-pros {
  background: rgba(22, 163, 74, 0.08);
  border-color: rgba(22, 163, 74, 0.16);
}

.col-cons {
  background: rgba(220, 38, 38, 0.06);
  border-color: rgba(220, 38, 38, 0.14);
}

.col-pros h4 {
  color: #15803d;
}

.col-cons h4 {
  color: #b91c1c;
}

.product-cols h4 {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.product-cols ul {
  margin: 0;
  padding-left: 18px;
}

.product-cols li {
  margin: 0 0 4px;
  line-height: 1.6;
}

.myth-card {
  margin: 10px 0 14px;
  padding: 14px 16px;
  border-left: 3px solid var(--vpn-teal);
  border-radius: 0 12px 12px 0;
  background: var(--vpn-surface-soft);
}

.myth-card h3 {
  margin-top: 0;
  margin-bottom: 6px;
}

.myth-card p {
  margin-bottom: 0;
}

.faq-section {
  margin-top: 36px;
  padding-top: 28px;
  border-top: 1px solid rgba(15, 48, 74, 0.06);
}

.section-label h2 {
  margin-top: 0;
  margin-bottom: 6px;
}

.section-label p {
  margin-bottom: 18px;
  color: var(--vpn-muted);
  font-size: 15px;
}

.faq-list {
  display: grid;
  gap: 12px;
}

.faq-item {
  padding: 18px 20px;
  border: 1px solid var(--vpn-line);
  border-radius: 16px;
  background: linear-gradient(180deg, #fff, #faf9ff);
}

.faq-item h3 {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 800;
  color: var(--vpn-ink);
}

.faq-item p {
  margin: 0;
}

.conclusion {
  margin-top: 28px;
  padding: 24px 24px 8px;
  border-radius: 18px;
  background:
    linear-gradient(135deg, rgba(91, 79, 232, 0.1), rgba(59, 105, 255, 0.05)),
    #f7f6ff;
  border: 1px solid rgba(91, 79, 232, 0.12);
}

.conclusion h2 {
  margin-top: 0;
}

.article-footer {
  margin-top: 32px;
  padding-top: 22px;
  border-top: 1px solid rgba(15, 48, 74, 0.06);
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 800;
  color: var(--vpn-teal-deep);
  text-decoration: none;
  transition: color 0.2s ease, gap 0.2s ease;
}

.back-link svg {
  width: 16px;
  height: 16px;
}

.back-link:hover {
  color: var(--vpn-teal);
}

@media (max-width: 720px) {
  .container {
    padding: 20px 0 64px;
  }

  .article {
    padding: 26px 18px 24px;
    border-radius: 18px;
  }

  .lead {
    font-size: 16px;
    padding: 14px 16px;
  }

  .product-cols {
    grid-template-columns: 1fr;
  }

  .conclusion {
    padding: 18px 16px 4px;
  }
}
</style>
